import classNames from 'classnames';
import { AnytimeSaleProduct } from '../Queries/useAnytimeProductList';

interface Props {
	product: AnytimeSaleProduct;
	isSelected: boolean;
	quantity?: number;
	onQuantityChange?: (quantity: number) => void;
	onSelect: (productId: string) => void;
}

export default function AnytimeTicketProductItem({
	product,
	isSelected,
	quantity = 1,
	onQuantityChange,
	onSelect,
}: Props) {
	const isPos = product.type === 'POS';
	// POS items are always staff-only; tickets are staff-only when flagged.
	const isRestricted = isPos || product.isRestricted === true;

	const handleSelectOrStop = (e: React.MouseEvent | React.KeyboardEvent) => {
		if (!isSelected) {
			onSelect(product.id);
		}
	};

	return (
		<div
			data-test-id="anytime-ticket-product-card"
			className={classNames(
				'flex flex-col px-6 py-5 rounded-xl border-2 transition-all duration-200 ease-in-out cursor-pointer',
				{
					'shadow-md border-[var(--colour-brand-primary)] bg-[color-mix(in_srgb,var(--colour-brand-primary),white_95%)]':
						isSelected,
					'bg-white border-gray-200 shadow-sm hover:border-gray-300': !isSelected,
				},
			)}
			onClick={handleSelectOrStop}
			role="button"
			tabIndex={0}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					handleSelectOrStop(e);
				}
			}}
		>
			<div className="flex items-center justify-between w-full">
				<span className="flex items-center gap-2">
					<span
						className={classNames('text-[1.1rem] transition-colors duration-200', {
							'font-semibold text-gray-900': isSelected,
							'font-medium text-gray-600': !isSelected,
						})}
					>
						{product.name}
					</span>
					{isRestricted && (
						<span
							data-test-id="anytime-ticket-staff-only-badge"
							className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700"
						>
							Staff only
						</span>
					)}
				</span>
				{product.priceOverrideName ? (
					<span className="flex items-baseline gap-2">
						<span className="text-sm text-gray-400 line-through">${product.basePrice.toFixed(2)}</span>
						<span className="text-[1.1rem] font-bold text-gray-900">${product.price.toFixed(2)}</span>
					</span>
				) : (
					<span className="text-[1.1rem] font-bold transition-colors duration-200 text-gray-900">
						${product.price.toFixed(2)}
					</span>
				)}
			</div>
			{product.priceOverrideName && (
				<div className="mt-1 self-end text-xs font-medium text-[var(--colour-brand-primary)]">
					{product.priceOverrideName} rate applied
				</div>
			)}
		</div>
	);
}
